import { useState, useEffect, useContext } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { UserProfileContext } from '../../App';
import { canView } from '../../utilities/Permissions';

const Sidebar = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    const savedPreferences = localStorage.getItem('user_preferences');
    const preferences = savedPreferences ? JSON.parse(savedPreferences) : {};
    return preferences.sidebar_expanded ?? false;
  });
  const { userProfile: currentUser } = useContext(UserProfileContext);

  useEffect(() => {
    const savedPreferences = localStorage.getItem('user_preferences');
    const preferences = savedPreferences ? JSON.parse(savedPreferences) : {};
    preferences.sidebar_expanded = isExpanded;
    localStorage.setItem('user_preferences', JSON.stringify(preferences));
  }, [isExpanded]);

  const renderMenuItem = item => {
    if (!canView(currentUser, item.entityType)) {
      return null;
    }

    return (
      <li key={item.name}>
        <NavLink
          to={item.path}
          // Collapsed, the icon is all you see, so the name shows on hover
          title={isExpanded ? undefined : item.name}
          className={({ isActive }) => `
            flex items-center px-2 py-1.5 rounded-lg transition-colors duration-200 w-full min-w-10
            ${
              isActive
                ? 'bg-white/15 text-white font-medium'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }
          `}
        >
          <div className='w-6 h-6 flex items-center justify-center'>
            <item.icon size={18} strokeWidth={1.75} className='flex-shrink-0' />
          </div>
          {isExpanded && (
            <span className='ml-2.5 mr-0 text-sm leading-6 whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out max-w-[240px]'>
              {item.name}
            </span>
          )}
        </NavLink>
      </li>
    );
  };

  return (
    <div
      id='sideNav'
      className={`bg-brand-700 h-screen ${
        isExpanded ? 'w-64' : 'w-16'
      } transition-all duration-300 ease-in-out flex flex-col `}
    >
      <div className={`flex h-12 shrink-0 items-center p-4 `}>
        <NavLink
          to='/'
          className='flex items-center justify-center h-full w-full'
        >
          <div className='relative h-10 w-full overflow-hidden'>
            <img
              alt='PTS'
              src='/logo-full-white.png'
              className='absolute top-1 left-0  min-w-[80px] max-w-[150px] overflow-hidden'
            />
          </div>
        </NavLink>
      </div>
      <div className='flex-grow overflow-y-auto scroller-brand'>
        <nav>
          <ul className='flex flex-col gap-0.5 py-2 px-3 overflow-hidden'>
            {items.map(renderMenuItem)}
          </ul>
        </nav>
      </div>
      <div className='mt-auto flex flex-col py-2 px-3 border-t border-white/10'>
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          title={isExpanded ? undefined : 'Expand'}
          className='flex items-center px-2 py-1.5 rounded-lg transition-colors duration-200 w-full cursor-pointer text-white/70 hover:bg-white/10 hover:text-white text-sm leading-6'
        >
          <div className='w-6 h-6 flex items-center justify-center'>
            {isExpanded ? (
              <ChevronLeft size={18} strokeWidth={1.75} className='flex-shrink-0' />
            ) : (
              <ChevronRight size={18} strokeWidth={1.75} className='flex-shrink-0' />
            )}
          </div>
          {isExpanded && (
            <span className='ml-2.5 mr-2 whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out max-w-[200px]'>
              {isExpanded ? 'Collapse' : 'Expand'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
